import type { ContentMembership, PartnerAccessLevel } from '@optimus/models';
import {
  CONTENT_MEMBERSHIP_BY_TIER,
  CONTENT_MEMBERSHIP_LEVEL_BY_NAME,
  PAID_BY_TIER,
  PARTNER_ACCESS_BY_TIER,
  PARTNER_ACCESS_RANK_BY_LEVEL,
  SELF_SERVE_CONTENT_MEMBERSHIPS,
  SELF_SERVE_PARTNER_ACCESS_LEVELS,
} from '@optimus/utils/src/membershipTierMaps';
import { SubscriptionTier } from '../models/Subscription';

/**
 * Browser-side facade over the membership tier maps in
 * `@optimus/utils/src/membershipTierMaps`, mirroring `MembershipTierAccess` in
 * `@optimus/models` function for function so a call site can be moved from one
 * to the other unchanged.
 *
 * The maps themselves are NOT duplicated here: that file is plain JavaScript
 * precisely so both sides can read the same rows. It replaced the hand-maintained
 * `TIER_RANK` in `PartnerMembershipTierUtils`, which was a second copy of the
 * partner domain and put Association on the Roundtable rung; the Partners chart on
 * "SN Logic on the Edge platform" gives Association a rung of its own, which is
 * what these maps follow (SN-996). Only the types are re-attached
 * here, against the backend's own type aliases via a type-only import, which
 * Babel erases - a runtime import of `@optimus/models` would pull untranspiled
 * workspace TypeScript into Create React App's Webpack build.
 *
 * Each map is annotated as a total `Record<SubscriptionTier, …>` before use, so
 * a tier added to the frontend enum without a row in the shared file fails the
 * build here as well as on the backend.
 *
 * Tiers are grouped per domain and two tiers in the same group are equal in that
 * domain: Association matches Edge for content while holding its own partner rung
 * between Edge and Roundtable. Never compare `SubscriptionTier` values directly,
 * and never read `tier !== EDGE` as "paid" - `isPaidTier` is that check.
 */

const paidByTier: Record<SubscriptionTier, boolean> = PAID_BY_TIER;
const partnerAccessByTier: Record<SubscriptionTier, string> = PARTNER_ACCESS_BY_TIER;
const contentMembershipByTier: Record<SubscriptionTier, string> = CONTENT_MEMBERSHIP_BY_TIER;

export const PAID_TIERS = paidByTier;

export const SUBSCRIPTION_TIER_PARTNER_ACCESS =
  partnerAccessByTier as Record<SubscriptionTier, PartnerAccessLevel>;

export const SUBSCRIPTION_TIER_MEMBERSHIPS =
  contentMembershipByTier as Record<SubscriptionTier, ContentMembership>;

export const PARTNER_ACCESS_RANKS: Record<PartnerAccessLevel, number> =
  PARTNER_ACCESS_RANK_BY_LEVEL;

export const MEMBERSHIP_LEVELS: Record<ContentMembership, number> =
  CONTENT_MEMBERSHIP_LEVEL_BY_NAME;

export const SELF_SERVE_PARTNER_ACCESS =
  SELF_SERVE_PARTNER_ACCESS_LEVELS as PartnerAccessLevel[];

export const SELF_SERVE_CONTENT_LEVELS =
  SELF_SERVE_CONTENT_MEMBERSHIPS as ContentMembership[];

/** Members without a subscription are treated as Edge, ie. Free/Foundation. */
export const DEFAULT_PARTNER_ACCESS: PartnerAccessLevel = 'Free';
export const DEFAULT_MEMBERSHIP: ContentMembership = 'Foundation';

/**
 * Whether a member on `tier` holds a paying membership. A member with no
 * subscription is on the free tier, so `false`.
 *
 * Association is paid (SN-996). This replaced `isFreeMembershipTier`, which
 * counted Association as free and so had every partner call site special-case it.
 */
export const isPaidTier = (tier?: SubscriptionTier | null): boolean => (
  !!tier && (PAID_TIERS[tier] ?? false)
);

/**
 * Whether a member on `tier` should see an "Upgrade" call to action for
 * Memberships. Paid and upgradeable are separate rules: Association is paid
 * (`isPaidTier`) yet Roundtable is still a genuine upgrade for it, unlike
 * every tier at or above Roundtable.
 */
export const canUpgradeTier = (tier?: SubscriptionTier | null): boolean => (
  !isPaidTier(tier) || tier === SubscriptionTier.AM
);

/** The partner access group a member holding `tier` is granted. */
export const partnerAccessOfTier = (
  tier?: SubscriptionTier | null,
): PartnerAccessLevel => (tier && SUBSCRIPTION_TIER_PARTNER_ACCESS[tier]) || DEFAULT_PARTNER_ACCESS;

/** Rank of the partner access group `tier` belongs to. */
export const partnerAccessRankOfTier = (tier?: SubscriptionTier | null): number => (
  PARTNER_ACCESS_RANKS[partnerAccessOfTier(tier)]
);

/**
 * Whether `access` is too low to reach partners requiring `required`. Tiers in
 * the same group are not below one another, so an ADVP member is not below a
 * partner requiring Advanced. Association *is* below a partner requiring
 * Roundtable: it holds its own rung.
 */
export const isPartnerAccessBelow = (
  access: PartnerAccessLevel,
  required: PartnerAccessLevel,
): boolean => PARTNER_ACCESS_RANKS[access] < PARTNER_ACCESS_RANKS[required];

/**
 * Whether a member on `memberTier` may see a partner whose `membershipType` is
 * `requiredTier`. A partner with no `membershipType` is open to everyone.
 */
export const meetsPartnerAccess = (
  memberTier?: SubscriptionTier | null,
  requiredTier?: SubscriptionTier | null,
): boolean => {
  if (!requiredTier) return true;
  return !isPartnerAccessBelow(
    partnerAccessOfTier(memberTier),
    partnerAccessOfTier(requiredTier),
  );
};

/**
 * Every tier whose partner access is at or below `tier`'s, ie. the tiers a
 * member on `tier` may see partners for. Use this to ask the API for a partner
 * list instead of sending "edge or srt".
 */
export const partnerTiersVisibleTo = (tier?: SubscriptionTier | null): SubscriptionTier[] => {
  const rank = partnerAccessRankOfTier(tier);
  return (Object.keys(SUBSCRIPTION_TIER_PARTNER_ACCESS) as SubscriptionTier[])
    .filter((candidate) => partnerAccessRankOfTier(candidate) <= rank);
};

/**
 * Whether reaching `tier` needs a sales conversation, ie. it sits above the
 * highest tier sold in app. Use it to choose between an "Upgrade" and a
 * "Contact Sales" call to action instead of listing tiers at the call site.
 */
export const isSalesAssistedTier = (tier?: SubscriptionTier | null): boolean => (
  !SELF_SERVE_PARTNER_ACCESS.includes(partnerAccessOfTier(tier))
);

/**
 * Whether a locked partner should offer "Contact Sales" rather than "Upgrade":
 * the member does not reach the partner's tier and that tier cannot be bought in
 * app. A partner with no `membershipType` is open to everyone, so neither call to
 * action applies.
 */
export const shouldShowContactSales = (
  memberTier?: SubscriptionTier | null,
  requiredTier?: SubscriptionTier | null,
): boolean => (
  !!requiredTier &&
  isSalesAssistedTier(requiredTier) &&
  !meetsPartnerAccess(memberTier, requiredTier)
);

/** The content membership a member holding `tier` is granted. */
export const membershipOfSubscriptionTier = (
  tier?: SubscriptionTier | null,
): ContentMembership => (tier && SUBSCRIPTION_TIER_MEMBERSHIPS[tier]) || DEFAULT_MEMBERSHIP;

/** Whether `membership` is too low to access content requiring `required`. */
export const isMembershipBelow = (
  membership: ContentMembership,
  required: ContentMembership,
): boolean => MEMBERSHIP_LEVELS[membership] < MEMBERSHIP_LEVELS[required];

/**
 * Every tier whose content membership is strictly above `membership`, ie. the
 * tiers a piece of content tagged one level higher would open up to. Use it to
 * build an allow list from the content map instead of hand-listing tiers, which
 * is how the lists in `useHasAccess` are kept in step with the tiers.
 */
export const tiersAboveContentMembership = (
  membership: ContentMembership,
): SubscriptionTier[] => (Object.keys(SUBSCRIPTION_TIER_MEMBERSHIPS) as SubscriptionTier[])
  .filter((tier) => isMembershipBelow(membership, membershipOfSubscriptionTier(tier)));

/**
 * Whether a member on `tier` may read content tagged `required`. Edge and
 * Association are interchangeable here: both read Foundation only.
 */
export const meetsContentMembership = (
  tier: SubscriptionTier | undefined | null,
  required?: ContentMembership | null,
): boolean => {
  if (!required) return true;
  return !isMembershipBelow(membershipOfSubscriptionTier(tier), required);
};

/**
 * Whether reaching `membership` needs a sales conversation, ie. it is granted only
 * by a tier above the highest one sold in app. The content-domain counterpart of
 * `isSalesAssistedTier`.
 */
export const isSalesAssistedContentMembership = (
  membership?: ContentMembership | null,
): boolean => !!membership && !SELF_SERVE_CONTENT_LEVELS.includes(membership);

/**
 * Whether locked content should offer "Contact Sales" rather than "Upgrade to
 * unlock": the member's membership does not reach the content's and that
 * membership cannot be bought in app. Untagged content is open to everyone, so
 * neither call to action applies.
 */
export const shouldShowContentContactSales = (
  tier?: SubscriptionTier | null,
  required?: ContentMembership | null,
): boolean => (
  isSalesAssistedContentMembership(required) &&
  !meetsContentMembership(tier, required)
);
